import { useQuery } from '@tanstack/react-query';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router';
import { useAuth } from '../auth/AuthContext';
import { StartChecklist } from '../components/StartChecklist';
import { Badge, ErrorNote } from '../components/ui';
import { PageHeader, useShortcuts } from '../layout/AppShell';
import { api } from '../lib/api';
import { fmtDate, qty } from '../lib/format';
import { STATUS_LABELS, type Project } from '../lib/types';

interface Dashboard {
  projects: { total: number; draft: number; inProgress: number; done: number; archived: number };
  quotations: { total: number; finalized: number; drafts: number };
  clients: number;
  finalizedByCurrency: Array<{ currency: string; quotations: number; ht: string }>;
  billing: Array<{ currency: string; billed: string; collected: string; outstanding: string; overdue: string; overdueCount: number }>;
  recentProjects: Array<{ id: string; reference: string; name: string; status: Project['status']; clientName: string | null; updatedAt: string }>;
}

/** XAF et XOF s'écrivent tous deux FCFA mais restent deux monnaies : le code est toujours affiché. */
const currencyLabel = (c: string) => (c === 'XAF' || c === 'XOF' ? `FCFA (${c})` : c);

export function DashboardPage() {
  const { user, can } = useAuth();
  const shortcuts = useShortcuts();
  const simple = user?.uiMode === 'SIMPLE';
  const canRead = can('project:read');
  const d = useQuery({ queryKey: ['dashboard'], queryFn: () => api<Dashboard>('/dashboard'), enabled: canRead });
  const data = d.data;

  return (
    <>
      <PageHeader title={`Bonjour ${user?.fullName.split(' ')[0] ?? ''}`} subtitle={user?.companyName} />
      <ErrorNote error={d.error} />

      <div className="mb-6 flex flex-wrap gap-2">
        {shortcuts.map((s, i) => (
          <Link key={s.to} to={s.to} className={i === 0 ? 'btn-accent' : 'btn-secondary'}>
            <s.icon size={16} /> {s.label}
          </Link>
        ))}
      </div>

      {data && <StartChecklist data={data} />}

      {data && (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {!simple && <Kpi label="Projets" value={String(data.projects.total)} hint={`en cours : ${data.projects.inProgress} · terminés : ${data.projects.done}`} to="/projets" />}
          <Kpi label="Devis" value={String(data.quotations.total)} hint={`brouillons : ${data.quotations.drafts} · finalisés : ${data.quotations.finalized}`} to="/devis" />
          <Kpi label="Clients" value={String(data.clients)} hint="fiches actives" to="/clients" />
          <div className="card p-4">
            <div className="text-xs font-semibold tracking-wide text-muted uppercase">Devis finalisés (HT)</div>
            {data.finalizedByCurrency.length === 0 && <div className="mt-2 text-sm text-muted">Aucun devis finalisé pour le moment.</div>}
            {data.finalizedByCurrency.map((f) => (
              <div key={f.currency} className="mt-2">
                <div className="text-xl font-bold text-navy-900 tabular-nums">{qty(f.ht, 0)} <span className="text-sm font-semibold">{currencyLabel(f.currency)}</span></div>
                <div className="text-xs text-muted">{f.quotations} devis</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {data && data.billing.length > 0 && (
        <section className="card mt-6 p-5">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-navy-900">Facturation</h2>
            <Link to="/factures" className="flex items-center gap-1 text-sm font-medium text-tech-600 hover:underline">Toutes les factures <ArrowRight size={14} /></Link>
          </div>
          {data.billing.map((b) => (
            <dl key={b.currency} className="mt-3 grid gap-3 sm:grid-cols-4">
              <div><dt className="text-xs text-muted">Facturé ({currencyLabel(b.currency)})</dt><dd className="text-lg font-bold text-navy-900 tabular-nums">{qty(b.billed, 0)}</dd></div>
              <div><dt className="text-xs text-muted">Encaissé</dt><dd className="text-lg font-bold text-ok tabular-nums">{qty(b.collected, 0)}</dd></div>
              <div><dt className="text-xs text-muted">Reste à encaisser</dt><dd className="text-lg font-bold text-navy-900 tabular-nums">{qty(b.outstanding, 0)}</dd></div>
              <div><dt className="text-xs text-muted">En retard ({b.overdueCount})</dt><dd className={`text-lg font-bold tabular-nums ${b.overdueCount ? 'text-bad' : 'text-muted'}`}>{qty(b.overdue, 0)}</dd></div>
            </dl>
          ))}
        </section>
      )}

      {data && !simple && (
        <section className="card mt-6">
          <div className="flex items-center justify-between border-b border-line px-5 py-3">
            <h2 className="font-semibold text-navy-900">Projets récents</h2>
            <Link to="/projets" className="flex items-center gap-1 text-sm font-medium text-tech-600 hover:underline">Tous les projets <ArrowRight size={14} /></Link>
          </div>
          {data.recentProjects.length === 0 ? (
            <p className="px-5 py-8 text-center text-sm text-muted">Aucun projet pour le moment. Commencez par « Nouveau projet » ou par un devis rapide.</p>
          ) : (
            <ul className="divide-y divide-line">
              {data.recentProjects.map((p) => (
                <li key={p.id}>
                  <Link to={`/projets/${p.id}`} className="flex flex-wrap items-center justify-between gap-2 px-5 py-3 hover:bg-surface">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold text-navy-900">{p.name}</div>
                      <div className="text-xs text-muted">{[p.reference, p.clientName, `modifié le ${fmtDate(p.updatedAt)}`].filter(Boolean).join(' · ')}</div>
                    </div>
                    <Badge tone={p.status === 'DONE' ? 'ok' : p.status === 'IN_PROGRESS' ? 'info' : 'neutral'}>{STATUS_LABELS[p.status]}</Badge>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </>
  );
}

function Kpi({ label, value, hint, to }: { label: string; value: string; hint: string; to: string }) {
  return (
    <Link to={to} className="card p-4 transition hover:border-tech-500">
      <div className="text-xs font-semibold tracking-wide text-muted uppercase">{label}</div>
      <div className="mt-2 text-2xl font-bold text-navy-900 tabular-nums">{value}</div>
      <div className="mt-1 text-xs text-muted">{hint}</div>
    </Link>
  );
}
